import type { Entity } from '@backstage/catalog-model';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { ViewMode } from '../../../shortcuts/types';
import { DOCUMENTATION_TABS } from '../../constants';
import { getDocumentationType } from '../../docsUtils';
import { DocsEmptyState } from '../atoms/DocsEmptyState';
import { DocsCard } from '../molecules/DocsCard';
import { useMemo } from 'react';

type Props = {
  entities: Entity[];
  groupByType: boolean;
  viewMode: ViewMode;
};

type GroupedEntity = {
  label: string;
  accent: string;
  entities: Entity[];
};

const getEntityKey = (entity: Entity): string =>
  `${entity.kind}:${entity.metadata.namespace || 'default'}/${
    entity.metadata.name
  }`;

export const DocsList = ({ entities, groupByType, viewMode }: Props) => {
  const classes = useStyles();
  const cardsClassName = `${classes.cards} ${
    viewMode === ViewMode.LIST ? classes.list : ''
  }`;

  const groupedEntities = useMemo(() => {
    return DOCUMENTATION_TABS.map(tab => {
      return entities.reduce(
        (acc, entity) => {
          if (getDocumentationType(entity) === tab.key) {
            acc.entities.push(entity);
          }
          return acc;
        },
        { label: tab.label, accent: tab.accent, entities: [] } as GroupedEntity,
      );
    });
  }, [entities]);

  if (entities.length === 0) {
    return <DocsEmptyState />;
  }

  if (!groupByType) {
    return (
      <Box className={cardsClassName}>
        {entities.map(entity => (
          <DocsCard key={getEntityKey(entity)} entity={entity} />
        ))}
      </Box>
    );
  }

  return (
    <Box className={classes.groups}>
      {groupedEntities.map(data => {
        if (data.entities.length === 0) {
          return null;
        }

        return (
          <Box key={data.label} component="section">
            <Box className={classes.groupHeader}>
              <span
                aria-hidden="true"
                className={classes.groupMarker}
                style={{ backgroundColor: data.accent }}
              />
              <Typography className={classes.groupTitle} component="h2">
                {data.label}
              </Typography>
              <Typography className={classes.groupCount} component="span">
                {data.entities.length}{' '}
                {data.entities.length === 1 ? 'doc' : 'docs'}
              </Typography>
            </Box>
            <Box className={cardsClassName}>
              {data.entities.map(entity => (
                <DocsCard key={getEntityKey(entity)} entity={entity} />
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  cards: {
    display: 'grid',
    gap: theme.spacing(1.5),
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  groupCount: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 600,
  },
  groupHeader: {
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.border}`,
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
  },
  groupMarker: {
    borderRadius: 2,
    height: 14,
    width: 14,
  },
  groups: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
  },
  groupTitle: {
    color: theme.palette.text.primary,
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1.25,
    margin: 0,
  },
  list: {
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
}));
